import { useRef, useState } from 'react';
import Layout from '@/components/Layout';
import { API } from '@/lib/api';
import { Sparkles, Send } from 'lucide-react';

export default function AITutor() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Salut, je suis Maestro. Dis-moi ce que tu apprends aujourd'hui — un morceau, une gamme, un accord... Je suis là pour t'aider." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`tutor-${Date.now()}`);
  const endRef = useRef(null);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setBusy(true);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      if (!resp.ok) {
        // Handle 402 (plan limit)
        if (resp.status === 402) {
          try {
            const err = await resp.json();
            const msg = err?.detail?.message || 'Limite atteinte.';
            setMessages(m => {
              const arr = [...m];
              arr[arr.length - 1] = { role: 'assistant', content: `${msg}\n\nPasse Pro pour un accès illimité → /pro`, limited: true };
              return arr;
            });
          } catch {}
          setBusy(false); return;
        }
      }
      if (!resp.body) throw new Error('No stream');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          if (!part.startsWith('data:')) continue;
          const payload = part.slice(5).trimStart();
          if (payload === '[DONE]') continue;
          if (payload.startsWith('[ERROR]')) {
            setMessages(m => {
              const arr = [...m]; arr[arr.length - 1] = { role: 'assistant', content: 'Une erreur est survenue.' }; return arr;
            });
            continue;
          }
          setMessages(m => {
            const arr = [...m];
            const last = arr[arr.length - 1];
            arr[arr.length - 1] = { ...last, content: (last.content || '') + payload };
            return arr;
          });
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-3xl">
        <div className="mb-6 rise flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-orange-700/20 border border-orange-800/40 flex items-center justify-center">
            <Sparkles className="text-orange-400" />
          </div>
          <div>
            <div className="overline text-orange-400">tuteur ia · claude sonnet 5</div>
            <h1 className="font-display text-4xl leading-none">Maestro</h1>
          </div>
        </div>

        <div data-testid="chat-window" className="card-stone p-4 md:p-6 min-h-[50vh] max-h-[65vh] overflow-y-auto space-y-4 mb-4">
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`chat-msg-${i}`}
              className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 leading-relaxed text-sm ${
                m.role === 'user' ? 'bg-orange-700 text-white ml-auto' : 'bg-stone-800 text-stone-100'
              }`}
            >
              {m.content || (busy && i === messages.length - 1 ? '...' : '')}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="flex items-end gap-2">
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Explique-moi le rythme de Take Five..."
            rows={2}
            className="flex-1 resize-none px-4 py-3 rounded-2xl bg-stone-900 border border-stone-800 text-stone-100 placeholder-stone-500 focus:outline-none focus:border-orange-800"
          />
          <button data-testid="chat-send" onClick={send} disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            <Send size={16} /> Envoyer
          </button>
        </div>
        <p className="text-xs text-stone-500 mt-2">Astuce : demande une progression d'accords, une technique, ou un plan de pratique.</p>
      </div>
    </Layout>
  );
}
